/* eslint-disable unicorn/no-nested-ternary */

import {
  DateFormats,
  DescriptionList,
  type DescriptionListItem,
  type DescriptionListProps,
  formatISODate,
  formatNumber,
  RedactedWrapper,
  Skeleton,
} from '@omnipresentgroup/design-system';
import {
  type ExpensePermissions,
  useEmployeePermissions,
} from '@omnipresentgroup/shared';
import {
  type Expense,
  ExpenseStatusEnum,
  getExpensesDates,
} from '@omnipresentgroup/types';
import { useMemo } from 'react';

import { DownloadButton } from '../../documents';
import { ExpenseCategory } from '../expense-category';
import { ExpenseStatus } from '../expense-status';

type Renderer = (
  expense: Expense,
  permissions?: ExpensePermissions,
) => string | JSX.Element;

const EXPENSE_ENTRIES = [
  { key: 'title', label: 'Spent at' },
  { key: 'status', label: 'Status' },
  { key: 'description', label: 'Description' },
  { key: 'category', label: 'Category' },
  { key: 'amount', label: 'Amount spent' },
  { key: 'dateSpent', label: 'Date spent' },
  { key: 'proofOfPurchase', label: 'Proof of purchase' },
];

const statusToLabelMapping: Partial<Record<ExpenseStatusEnum, string>> = {
  [ExpenseStatusEnum.APPROVED]: 'Approved by',
  [ExpenseStatusEnum.REJECTED]: 'Rejected by',
  [ExpenseStatusEnum.INFO_NEEDED]: 'Reviewed By',
  [ExpenseStatusEnum.PAID]: 'Reviewed By',
};

const Render = ({
  value,
  children,
}: {
  value: string | number;
  children?: JSX.Element | string;
}) => <RedactedWrapper value={value}>{children || value}</RedactedWrapper>;

const renderers: Record<string, Renderer> = {
  amount: (expense) => (
    <Render value={expense.amount.amount}>
      {formatNumber({
        input: Number(expense.amount.amount),
        currency: expense.amount.currency,
      })}
    </Render>
  ),
  dateSpent: (expense) => (
    <Render value={expense.dateSpent}>
      {formatISODate(expense.dateSpent, DateFormats.Date, true, '')}
    </Render>
  ),
  description: (expense) => <Render value={expense.description} />,
  category: (expense) => (
    <Render value={expense.category}>
      <ExpenseCategory category={expense.category} />
    </Render>
  ),
  status: (expense) => (
    <Render value={expense.status}>
      <ExpenseStatus status={expense.status} />
    </Render>
  ),
  proofOfPurchase: (expense) => (
    <Render value={expense.status}>
      {expense.documentId ? (
        <DownloadButton id={expense.documentId} />
      ) : (
        'No document attached'
      )}
    </Render>
  ),
};

const getExpenseDetails = (
  expense: Expense,
  isLoading: boolean,
  permissions?: ExpensePermissions,
): DescriptionListItem[] =>
  EXPENSE_ENTRIES.map(({ label, key }) => ({
    header: label,
    detail: isLoading ? (
      <Skeleton key={key} />
    ) : (
      (renderers[key]?.(expense, permissions) ?? expense[key as keyof Expense])
    ),
  }));

const getAdditionalDetails = (expense: Expense): DescriptionListItem[] => {
  const { approvedDate, rejectDate, reportedDate } = getExpensesDates(
    expense.statusHistory,
  );

  return [
    statusToLabelMapping[expense.status] && {
      header: statusToLabelMapping[expense.status],
      detail: expense.approverName,
    },
    approvedDate && {
      header: 'Approved date',
      detail: formatISODate(approvedDate, DateFormats.Date, true, ''),
    },
    rejectDate && {
      header: 'Rejected date',
      detail: formatISODate(rejectDate, DateFormats.Date, true, ''),
    },
    reportedDate && {
      header: 'Reported date',
      detail: formatISODate(reportedDate, DateFormats.Date, true, ''),
    },
  ].filter(Boolean) as DescriptionListItem[];
};

export const ExpenseDetailsList = ({
  expense,
  isLoading,
  omnipresentTenantId,
  ...descriptionListProps
}: {
  expense: Expense;
  isLoading: boolean;
  omnipresentTenantId?: string;
} & Omit<DescriptionListProps, 'id' | 'list' | 'template'>) => {
  const { permissions } = useEmployeePermissions({
    employeeId: expense.employeeId,
    resourceType: 'expense',
    omnipresentTenantId,
  });

  const expenseDetails = useMemo(() => {
    const baseDetails = getExpenseDetails(expense, isLoading, permissions);
    const additionalDetails = getAdditionalDetails(expense);
    return [...baseDetails, ...additionalDetails];
  }, [expense, isLoading, permissions]);

  return (
    <div data-testid="expense-details-list">
      <DescriptionList
        fraction="1/2"
        id="expense-details-list"
        list={expenseDetails}
        {...descriptionListProps}
      />
    </div>
  );
};
